import React, { ReactElement } from 'react'
import {
  Card, ProgressBar, OverlayTrigger, Tooltip as BootstrapTooltip,
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NPSScoreDetails } from 'utils/npsCalculation'

interface NPSScoreCardProps {
  npsDetails: NPSScoreDetails
}

const NPS_TOOLTIP_TEXT = 'NPS is calculated from responses to a 0–10 recommendation question. Scores of 9–10 are "Promoters," 7–8 are "Passives," and 0–6 are "Detractors." The final score is the percentage of Promoters minus the percentage of Detractors, resulting in a value between −100 and +100.'

/**
 * Get color class based on NPS score
 * NPS industry benchmarks:
 * - Above 70: World-class (excellent)
 * - 50-69: Great
 * - 0-49: Good
 * - Below 0: Needs improvement
 */
const getNPSScoreColorClass = (nps: number): string => {
  if (nps >= 70) return 'text-success' // World-class NPS
  if (nps >= 50) return 'text-primary' // Great NPS
  if (nps >= 0) return 'text-info' // Good NPS (neutral to positive)
  if (nps >= -30) return 'text-warning' // Needs improvement
  return 'text-danger' // Critical attention needed
}

/**
 * Get border radius for progress bar segments
 * Handles edge cases where a segment might be the only one visible
 */
const getSegmentBorderRadius = (
  isFirst: boolean,
  isLast: boolean,
): string => {
  if (isFirst && isLast) return '4px' // Single segment - full border radius
  if (isFirst) return '4px 0 0 4px' // First segment - left radius
  if (isLast) return '0 4px 4px 0' // Last segment - right radius
  return '0' // Middle segment - no radius
}

export const NPSScoreCard: React.FC<NPSScoreCardProps> = ({
  npsDetails,
}): ReactElement => {
  const {
    nps, promoterPercentage, passivePercentage, detractorPercentage,
  } = npsDetails

  // Round percentages for display
  const promoterPct = Math.round(promoterPercentage)
  const passivePct = Math.round(passivePercentage)
  const detractorPct = Math.round(detractorPercentage)

  // Calculate NPS marker position (0 to 100 scale)
  // NPS ranges from -100 to +100, so we map it to 0-100%
  const npsMarkerPosition = ((nps + 100) / 200) * 100

  // Determine which segments are visible (have percentage > 0)
  // Use original floating point values for visibility check to handle small percentages
  const hasPromoters = promoterPercentage > 0
  const hasPassives = passivePercentage > 0
  const hasDetractors = detractorPercentage > 0

  // Calculate border radius for each segment based on visibility
  // Order: Detractors (left) -> Passives (middle) -> Promoters (right)
  // This aligns with NPS scale: -100 (left) to +100 (right)
  const detractorRadius = getSegmentBorderRadius(
    hasDetractors, // isFirst: Detractors is always first when visible
    !hasPassives && !hasPromoters, // isLast: only if no other segments
  )
  const passiveRadius = getSegmentBorderRadius(
    !hasDetractors, // isFirst: only if Detractors not visible
    !hasPromoters, // isLast: only if Promoters not visible
  )
  const promoterRadius = getSegmentBorderRadius(
    !hasDetractors && !hasPassives, // isFirst: only if no Detractors or Passives
    true, // isLast: Promoters is always last when visible
  )

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Body className="p-3">
        <div className="d-flex align-items-center justify-content-center mb-2 position-relative">
          <Card.Title className="text-muted small mb-0" style={{ fontSize: '0.875rem' }}>
            NET PROMOTER SCORE
          </Card.Title>
          <OverlayTrigger
            placement="top"
            overlay={(
              <BootstrapTooltip id="nps-tooltip" style={{ maxWidth: '300px' }}>
                {NPS_TOOLTIP_TEXT}
              </BootstrapTooltip>
            )}
          >
            <FontAwesomeIcon
              icon="question-circle"
              className="text-muted position-absolute"
              style={{ cursor: 'pointer', right: '0' }}
            />
          </OverlayTrigger>
        </div>

        <div className="mb-3 text-center">
          <span className={`h3 mb-0 ${getNPSScoreColorClass(nps)}`}>
            NPS:
            {' '}
            {nps}
          </span>
        </div>

        {/* Progress bar showing distribution with NPS marker */}
        <div className="position-relative" style={{ height: '14px' }}>
          <ProgressBar style={{ height: '14px', borderRadius: '4px' }}>
            <ProgressBar
              variant="danger"
              now={detractorPercentage}
              key="detractors"
              style={{ borderRadius: detractorRadius }}
            />
            <ProgressBar
              variant="secondary"
              now={passivePercentage}
              key="passives"
              style={{ borderRadius: passiveRadius }}
            />
            <ProgressBar
              variant="success"
              now={promoterPercentage}
              key="promoters"
              style={{ borderRadius: promoterRadius }}
            />
          </ProgressBar>
          {/* NPS marker line - extends above and below progress bar */}
          <div
            className="position-absolute"
            style={{
              left: `${npsMarkerPosition}%`,
              top: '-4px',
              bottom: '-4px',
              width: '4px',
              backgroundColor: '#000',
              transform: 'translateX(-50%)',
              zIndex: 10,
            }}
          />
        </div>

        {/* Percentage details - order matches progress bar: Detractors (left) -> Passives (middle) -> Promoters (right) */}
        <div className="d-flex justify-content-between mt-2 small">
          <div>
            <span className="text-danger font-weight-bold">Detractors: </span>
            <span>
              {detractorPct}
              %
            </span>
          </div>
          <div>
            <span className="text-secondary font-weight-bold">Passives: </span>
            <span>
              {passivePct}
              %
            </span>
          </div>
          <div>
            <span className="text-success font-weight-bold">Promoters: </span>
            <span>
              {promoterPct}
              %
            </span>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
